import WebsocketService from './websocket.service';
import ApiService from 'services/api/api.service';
import { GENERIC_NOTIFICATION } from 'constants/notifications';
import NotificationService from 'services/base/notifications.service';
import {
	PAGE_SIZE,
	parseNotificationsResponse,
} from 'helpers/notifications/notificationPayload';

export default class NotifyService extends WebsocketService {
	static instance = null;

	notificationsApiUrl = '/api/GenericEventConfiguration';
	notifications;
	error = null;
	loadingPage = null;

	static getInstance() {
		if (!NotifyService.instance) {
			NotifyService.instance = new NotifyService(true);
		}
		return NotifyService.instance;
	}

	static resetInstance() {
		super.resetInstance();
		NotifyService.instance = null;
	}

	constructor(internal = false) {
		super();

		if (!internal) {
			throw new Error('Use NotifyService.getInstance() instead of new.');
		}

		this.notifications = new NotificationService();
	}

	/**
	 * Fetch a single page of drawer notifications.
	 * @param {{ page?: number, pageSize?: number }} options
	 * @returns {Promise<{ items: object[], totalCount: number, unreadCount: number }>}
	 */
	async getPage({ page = 1, pageSize = PAGE_SIZE } = {}) {
		const res = await ApiService.notifications.getData(this.notificationsApiUrl, {
			params: {
				platformCall: true,
				page,
				pageSize,
			},
		});

		return parseNotificationsResponse(res?.data ?? {});
	}

	async getPageRetry(options = {}) {
		if (this.loadingPage) {
			return this.loadingPage;
		}

		this.loadingPage = (async () => {
			const MAX_RETRY = 3;
			const DELAY = 2000;
			for (let i = 0; i < MAX_RETRY; i++) {
				this.error = null;
				try {
					return await this.getPage(options);
				} catch (err) {
					this.error = err;
					if (i === MAX_RETRY - 1) {
						this.notifications.error('Load notifications failed', err?.message);
						throw err;
					}
					await this.sleep(DELAY);
				}
			}
		})();

		try {
			return await this.loadingPage;
		} finally {
			this.loadingPage = null;
		}
	}

	async clearAll() {
		try {
			const res = await ApiService.notifications.delete(this.notificationsApiUrl, {});
			this.events.publish(GENERIC_NOTIFICATION.event, { reset: true });
			return res.data;
		} catch (err) {
			this.notifications.error('Clear notifications failed', err?.message);
			throw err;
		}
	}

	async markAsRead() {
		try {
			const res = await ApiService.notifications.patch(`${this.notificationsApiUrl}/read`, {});
			return res.data;
		} catch (err) {
			this.notifications.error('Mark as read failed', err?.message);
			throw err;
		}
	}

	sleep(delay) {
		return new Promise((resolve) => setTimeout(resolve, delay));
	}

	async subscribe(listener) {
		this.events.subscribe(GENERIC_NOTIFICATION.event, listener);
	}

	unsubscribe(listener) {
		this.events.unsubscribe(GENERIC_NOTIFICATION.event, listener);
	}

	unsubscribeAll() {
		this.removeAllListeners();
		this.events.removeAllListeners(GENERIC_NOTIFICATION.event);
	}
}
